import { AnyAction } from "redux";
import { IFetchAction, FetchStatus } from "../middleware/fetch-middleware";
import { IUsernameAndPasswordData } from "../../create-account-app/components/username-and-password-step";
import { IIdentityData } from "../../create-account-app/components/identity-step";
import { IUserIdentityDto } from "../DTO/user-identity";
import { ISecurityQuestion } from "../DTO/security-question";
import { IUserDTO, IUserProfileDTO } from "../DTO/user";
import { INewUserDto } from "../DTO/new-user";
import { ICPGResponse } from "../DTO/responses";

export enum AccountApiActions {
    GetCurrentUser = 'accountApiActions/getCurrentUser',
    ValidateInviteToken = 'accountApiActions/validateInviteToken',
    VerifyTemporaryPassword = 'accountApiActions/verifyTemporaryPassword',
    VerifyWebAccountEligibility = 'accountApiActions/verifyWebAccountEligibility',
    SubmitLegalConsent = 'accountApiActions/submitLegalConsent',
    SubmitAccountCreate = 'accountApiActions/submitAccountCreate',
    SendEmailClientId = 'accountApiActions/sendEmailClientId',
    ResendEmailVerification = 'accountApiActions/resendEmailVerification',
    ActivateAccount = 'accountApiActions/activateAccount',
    FetchSecurityQuestions = 'accountApiActions/fetchSecurityQuestions',
    SubmitSecurityQuestionsData = 'accountApiActions/submitSecurityQuestionsData',
    UpdateUserProfile = 'accountApiActions/updateUserProfile',
}

export interface AccountApiState {
    currentUser?: IUserDTO
    userIsSponsored: boolean
    inviteToken?: string
    securityQuestions: Array<Array<ISecurityQuestion>>
    userProfileResult: ICPGResponse;
}

const DefaultAccountApiState: AccountApiState = {
    userIsSponsored: false,
    inviteToken: undefined,
    securityQuestions: [],
    userProfileResult: undefined
}

const BaseUrl = '/api/v1/accounts'

export const accountApiReducer = (state = DefaultAccountApiState, action: AnyAction): AccountApiState => {

    switch (action.type) {
        case AccountApiActions.ValidateInviteToken + FetchStatus.Complete:
            return {
                ...state,
                userIsSponsored: action.data.isSponsored,
                inviteToken: action.data.inviteToken
            }
 
        case AccountApiActions.SubmitSecurityQuestionsData + FetchStatus.Complete:
            return {
                ...state,
                securityQuestions: action.data
            }

        case AccountApiActions.GetCurrentUser + FetchStatus.Complete:
            return {
                ...state,
                currentUser: action.data
            }

        case AccountApiActions.FetchSecurityQuestions + FetchStatus.Complete:
            return {
                ...state,
                securityQuestions: action.data
            }
    }

    return state;
}

export const validateInviteToken = (token: string): IFetchAction => ({
    type: AccountApiActions.ValidateInviteToken,
    fetch: new Request(`${BaseUrl}/validate-token`, {
        method: 'POST',
        body: JSON.stringify({ value: token })
    })
})

export const verifyTemporaryPassword = (password: string): IFetchAction => ({
    type: AccountApiActions.VerifyTemporaryPassword,
    fetch: new Request(`${BaseUrl}/temp-password`, {
        method: 'POST',
        body: JSON.stringify({ value: password })
    })
})

export const verifyWebAccountEligibility = (data: IUserIdentityDto): IFetchAction => ({
    type: AccountApiActions.VerifyWebAccountEligibility,
    fetch: new Request(`${BaseUrl}/validate-web-account-eligibility`, {
        method: 'POST',
        body: JSON.stringify(data)
    })
})

export const sendEmailClientId = (data: IUserIdentityDto): IFetchAction => ({
    type: AccountApiActions.SendEmailClientId,
    fetch: new Request(`${BaseUrl}/client-id/email`, {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json; charset=utf-8' }),
        body: JSON.stringify({
            FirstName: data.firstName,
            LastName: data.lastName,
            DayOfBirth: data.dayOfBirth,
            MonthOfBirth: data.monthOfBirth,
            LastFourDigits: data.lastFourDigits
        })
    })
})

export const submitAccountCreate = (data: INewUserDto): IFetchAction => ({
    type: AccountApiActions.SubmitAccountCreate,
    fetch: new Request(`${BaseUrl}/`, {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json; charset=utf-8' }),
        body: JSON.stringify(data)
    })
})

export const submitLegalConsent = (): IFetchAction => ({
    type: AccountApiActions.SubmitLegalConsent,
    fetch: new Request(`${BaseUrl}/legal-consent`, {
        method: 'POST'
    })
})

export const resendEmailVerification = (email: string): IFetchAction => ({
    type: AccountApiActions.ResendEmailVerification,
    fetch: new Request(`${BaseUrl}/email-verification`, {
        method: 'POST',
        body: JSON.stringify({ value: email })
    })
})

export const activateAccount = (activationCode: string): IFetchAction => ({
    type: AccountApiActions,
    fetch: new Request(`${BaseUrl}/activate`, {
        method: 'POST',
        body: JSON.stringify({ value: activationCode })
    })
})

export const fetchSecurityQuestions = (): IFetchAction => ({
    type: AccountApiActions.FetchSecurityQuestions,
    fetch: new Request(`${BaseUrl}/security-questions`, {
        method: 'GET'
    })
})

export const submitSecurityQuestionsData = (data: [ISecurityQuestion, ISecurityQuestion, ISecurityQuestion]): IFetchAction => ({
    type: AccountApiActions.SubmitSecurityQuestionsData,
    fetch: new Request(`${BaseUrl}/submit-security-questions`, {
        method: 'POST',
        body: JSON.stringify({
            questionOne: data[0],
            questionTwo: data[1],
            questionThree: data[2]
        })
    })
})

export const getCurrentUser = (): IFetchAction => ({
    type: AccountApiActions.GetCurrentUser,
    fetch: new Request(`${BaseUrl}/me`, { method: 'GET' })
})

export const updateUserProfile = (data: IUserProfileDTO): IFetchAction => ({
    type: AccountApiActions.UpdateUserProfile,
    fetch: new Request(`${BaseUrl}/update-user-profile`, {
        method: 'POST',
        body: JSON.stringify(data)
    })
})